import { useState } from "react";
import { useI18n } from "../../i18n";
import { createId } from "../../lib/id";
import { mockTranslate, type TranslationPair } from "../../lib/mock";

interface TranslationRecord {
  id: string;
  pair: TranslationPair;
  input: string;
  output: string;
  createdAt: string;
}

const HISTORY_KEY = "deskflow.translationHistory";

function loadHistory(): TranslationRecord[] {
  try {
    return JSON.parse(
      localStorage.getItem(HISTORY_KEY) ?? "[]",
    ) as TranslationRecord[];
  } catch {
    return [];
  }
}

export function Translate(): React.JSX.Element {
  const { language, t } = useI18n();
  const [pair, setPair] = useState<TranslationPair>("en-zh");
  const [input, setInput] = useState("");
  const [output, setOutput] = useState("");
  const [history, setHistory] = useState(loadHistory);
  const [notice, setNotice] = useState("");

  const translate = (): void => {
    if (!input.trim()) return;
    const result = mockTranslate(input, pair);
    const record: TranslationRecord = {
      id: createId(),
      pair,
      input: input.trim(),
      output: result,
      createdAt: new Date().toISOString(),
    };
    const next = [record, ...history].slice(0, 5);
    setOutput(result);
    setHistory(next);
    localStorage.setItem(HISTORY_KEY, JSON.stringify(next));
    setNotice(t("translate.mockNotice"));
  };
  const swap = (): void => {
    setPair((current) => (current === "en-zh" ? "zh-en" : "en-zh"));
    if (output) {
      setInput(
        output.replace(/^【模拟译文】\s*|^\[Mock translation\]\s*/u, ""),
      );
      setOutput("");
    }
  };

  return (
    <div className="translation-layout">
      <section className="panel translation-workspace">
        <div className="translation-toolbar">
          <label>
            <span>{t("translate.pair")}</span>
            <select
              value={pair}
              onChange={(event) =>
                setPair(event.target.value as TranslationPair)
              }
            >
              <option value="en-zh">{t("translate.enZh")}</option>
              <option value="zh-en">{t("translate.zhEn")}</option>
            </select>
          </label>
          <button className="swap-button" type="button" onClick={swap}>
            ⇄ {t("translate.swap")}
          </button>
        </div>
        <div className="translation-panes">
          <label className="translation-pane">
            <span>{t("translate.source")}</span>
            <textarea
              value={input}
              onChange={(event) => setInput(event.target.value)}
              placeholder={t("translate.placeholder")}
            />
            <small>{input.length} / 5000</small>
          </label>
          <div className="translation-pane output-pane">
            <span>{t("translate.result")}</span>
            <div className="translation-output">
              {output || t("translate.empty")}
            </div>
            {output && (
              <button
                className="text-button"
                type="button"
                onClick={() =>
                  void navigator.clipboard
                    .writeText(output)
                    .then(() => setNotice(t("translate.copied")))
                }
              >
                {t("translate.copy")}
              </button>
            )}
          </div>
        </div>
        <div className="form-actions">
          <button
            className="secondary"
            type="button"
            onClick={() => {
              setInput("");
              setOutput("");
              setNotice("");
            }}
          >
            {t("translate.clear")}
          </button>
          <button
            className="primary"
            type="button"
            disabled={!input.trim()}
            onClick={translate}
          >
            {t("translate.translate")}
          </button>
        </div>
        {notice && <p className="notice info">{notice}</p>}
      </section>

      <aside className="panel translation-history">
        <small>{t("translate.localOnly")}</small>
        <h2>{t("translate.history")}</h2>
        {history.length === 0 ? (
          <p className="muted">{t("translate.noHistory")}</p>
        ) : (
          history.map((record) => (
            <button
              type="button"
              key={record.id}
              onClick={() => {
                setPair(record.pair);
                setInput(record.input);
                setOutput(record.output);
              }}
            >
              <strong>
                {record.pair === "en-zh"
                  ? t("translate.enZh")
                  : t("translate.zhEn")}
              </strong>
              <span>{record.input}</span>
              <small>
                {new Intl.DateTimeFormat(language, {
                  month: "short",
                  day: "numeric",
                  hour: "2-digit",
                  minute: "2-digit",
                }).format(new Date(record.createdAt))}
              </small>
            </button>
          ))
        )}
      </aside>
    </div>
  );
}
